<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import { numOrNull } from '@/lib/numField'
import type { AgentConfigBlock } from '@/api/hosting'
import { useDataStore } from '@/stores/data'

/**
 * **主機設定**編輯器（批112；1.0.44 改成「已掛載的設定樣板」）。
 *
 * 一張卡＝一份掛在這台主機上的樣板：標頭是樣板名＋`namespace` 徽章＋下發範圍，表身是
 * 欄位表（中文名／env 名／型別／🔒／值／預設），卡底是〈儲存此 namespace 的值〉。
 *
 * 三件事與一般表單不同：
 *
 * 1. **秘密欄位**：後端從來不回明文，只回頭尾遮罩（`AQ.…9xyz`）。所以秘密欄位的輸入框
 *    **一律留空**、把遮罩放在 placeholder。
 * 2. ★★ **「空值＝不改」**：沒有輸入新值就**不要把那個鍵送出去**。送空字串或送遮罩
 *    都會把真金鑰洗掉，而且畫面上完全看不出來——這是這支元件最重要的一條。
 *    要清掉請按該列的〈清除〉（送 `null`）。
 * 3. **不提供複製**：秘密的存在意義就是「設得進去、讀不出來」。加一顆複製鈕等於把
 *    三層讀取權限那條防線自己拆掉（而且也沒東西可複製——前端手上只有遮罩）。
 *
 * 值域（`min`／`max`／`options`）來自樣板宣告，這裡先擋一手讓人填得舒服；真正的校驗在
 * 後端 `services/agent_config.py::coerce_value`。
 */
/** 產品鍵 → 產品名（`scopeText`；呼叫端頁面已 `ensureProducts()`）。 */
const store = useDataStore()

const props = withDefaults(defineProps<{
  blocks: AgentConfigBlock[]
  /** 存檔中（父層控制）——避免連按。 */
  saving?: boolean
  /** 可不可以改（`require_admin`；這裡只是 UX，真閘門在後端）。 */
  editable?: boolean
  /**
   * 看的人進得去〈產品 › 設定樣板〉嗎（批141-R；那頁 `meta.roles: MANAGER`）。
   * 預設 true；管理員（admin）傳 false，空狀態的指路改成「請營運主管…」，
   * 免得指去一個點了會被擋回來的頁面。
   */
  canManageTemplates?: boolean
}>(), { canManageTemplates: true })
const emit = defineEmits<{
  save: [namespace: string, values: Record<string, unknown>]
  detach: [block: AgentConfigBlock]
  scope: [block: AgentConfigBlock]
}>()

/** 每個 namespace 各一份草稿：`draft[namespace][key]`（一律存字串，送出時再依 type 轉回去）。 */
const draft = ref<Record<string, Record<string, string>>>({})
/** 這一輪要不要清掉某個秘密欄位（送 `null`）。 */
const clearing = ref<Record<string, Record<string, boolean>>>({})

/** 同 namespace 的兩份樣板值共用一層 ⇒ 草稿也要以 namespace 為鍵、跨卡合併。 */
function initDrafts() {
  const next: Record<string, Record<string, string>> = {}
  const clr: Record<string, Record<string, boolean>> = {}
  for (const block of props.blocks) {
    const row = next[block.namespace] ?? {}
    for (const f of block.fields) {
      // ★ 秘密欄位一律空白（我們手上只有遮罩，填進去就會被當成新值送出去）
      row[f.key] = f.secret ? '' : f.value == null ? '' : String(f.value)
    }
    next[block.namespace] = row
    clr[block.namespace] = clr[block.namespace] ?? {}
  }
  draft.value = next
  clearing.value = clr
}

initDrafts()
// ★ 只在「樣板／欄位集合」變了才重建草稿——輪詢每 10 秒回一次同樣的 blocks，
//   拿 blocks 本身當 watch 來源會讓人打字打到一半被蓋掉（HostDetailView 的既有教訓）。
watch(
  () =>
    props.blocks
      .map((b) => `${b.template_id}:${b.namespace}:${b.fields.map((f) => f.key).join(',')}`)
      .join('|'),
  () => initDrafts(),
)

const canEdit = computed(() => props.editable !== false && !props.saving)

function toggleClear(namespace: string, key: string) {
  const row = clearing.value[namespace] ?? {}
  row[key] = !row[key]
  clearing.value[namespace] = { ...row }
  if (row[key]) draft.value[namespace][key] = ''
}

function outOfRange(block: AgentConfigBlock, key: string): boolean {
  const f = block.fields.find((x) => x.key === key)
  if (!f || f.type !== 'number') return false
  // ★ Vue 3 對 `<input type="number">` 自動套 `.number` ⇒ 一打字草稿就從字串變數字。
  //   一律走 `numOrNull`／`String()`，不要對它 `.trim()`（批106 的根因）。
  const n = numOrNull(draft.value[block.namespace]?.[key])
  if (n === null) return false
  if (f.min != null && n < f.min) return true
  return f.max != null && n > f.max
}

/** 欄位名稱的 tooltip：說明 ＋ 對應的環境變數（env 名不再獨立顯示，見 template 註解）。 */
function labelTitle(f: AgentConfigBlock['fields'][number]): string {
  return [f.hint, `環境變數 ${f.env_key}`].filter(Boolean).join('\n')
}

function rangeText(f: AgentConfigBlock['fields'][number]): string {
  if (f.type !== 'number') return ''
  if (f.min != null && f.max != null) return `${f.min}–${f.max}`
  if (f.max != null) return `≤ ${f.max}`
  return f.min != null ? `≥ ${f.min}` : ''
}

/**
 * 下發範圍的人話（`null`＝全部產品）。
 *
 * ★ 印**產品名**不是產品鍵（批121）：`iplusone-cs` 這種鍵是給程式看的，畫面上要寫
 *   「愛+1 社群工作台」。名稱查 store（資料驅動，不是硬編對照——LS 前端不准有產品字面）；
 *   產品清單還沒載回來或該產品已被刪時 `productName()` 會退回鍵，那比空白好。
 */
function scopeText(block: AgentConfigBlock): string {
  if (block.products == null) return '下發給：全部產品'
  if (!block.products.length) return '下發給：（無）'
  return `下發給：${block.products.map((k) => store.productName(k)).join('、')}`
}

/** 這個區塊有沒有「真的要送出去」的改動（純粹為了把〈儲存〉鈕變灰）。 */
function dirty(block: AgentConfigBlock): boolean {
  return Object.keys(buildValues(block)).length > 0
}

/**
 * 組出要送出的值。★★ 這裡就是「空值＝不改」那條規則的實作點：
 * - 秘密欄位：輸入框空的就**整個鍵不出現**；勾了〈清除〉才送 `null`。
 * - 非秘密欄位：與目前值相同就不送（少送一個鍵＝稽核紀錄少一筆雜訊）。
 */
function buildValues(block: AgentConfigBlock): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of block.fields) {
    if (f.deprecated) continue // 已停用的欄位不可編、也不下發
    // ★ `String(...)` 不可省：數字欄的草稿被 Vue 轉成 number 之後 `.trim()` 會當場炸
    //   （批106 的三張回報單全是這個根因）。
    const raw = String(draft.value[block.namespace]?.[f.key] ?? '').trim()
    if (f.secret) {
      if (clearing.value[block.namespace]?.[f.key]) out[f.key] = null
      else if (raw !== '') out[f.key] = raw
      continue
    }
    const current = f.value == null ? '' : String(f.value)
    if (raw === current) continue
    if (raw === '') {
      out[f.key] = null
      continue
    }
    out[f.key] =
      f.type === 'number' ? numOrNull(raw) : f.type === 'bool' ? raw === 'true' : raw
  }
  return out
}

function save(block: AgentConfigBlock) {
  const values = buildValues(block)
  if (!Object.keys(values).length) return
  emit('save', block.namespace, values)
  // ★ 送出後把秘密欄位的輸入框清空、並解除〈清除〉勾選。
  //   理由：存檔之後畫面會留著一個**顯示著明文金鑰**的輸入框（`blocks` 的欄位集合沒變
  //   ⇒ 上面那個 watch 不會重建草稿），一直到整頁重載為止——秘密的定義就是「設得進去、
  //   讀不出來」，畫面上不該有第二個地方存著它。
  //   ⚠️ 代價：存檔失敗時使用者要重打一次（父層會跳錯誤視窗，看得到）。這個方向的錯
  //   比反過來安全。非秘密欄位不動——那些值本來就會從後端的新投影回填。
  const row = { ...(draft.value[block.namespace] ?? {}) }
  for (const f of block.fields) if (f.secret) row[f.key] = ''
  draft.value[block.namespace] = row
  clearing.value[block.namespace] = {}
}
</script>

<template>
  <div class="ac">
    <p v-if="!blocks.length" class="ac-empty">
      <template v-if="canManageTemplates">
        尚未掛載任何樣板——先到〈產品 › 設定樣板〉匯入或建立，再按上方〈＋ 掛載樣板〉。
      </template>
      <template v-else>
        尚未掛載任何樣板——樣板由營運主管在〈產品 › 設定樣板〉匯入或建立；建好之後再按上方〈＋ 掛載樣板〉。
      </template>
    </p>

    <div v-for="block in blocks" :key="block.template_id ?? block.namespace" class="ac-block">
      <div class="ac-block-head">
        <strong>{{ block.name }}</strong>
        <code class="ns">{{ block.namespace }}</code>
        <small class="muted">{{ scopeText(block) }}</small>
        <small v-if="!block.effective_products.length" class="warn" title="這台主機自報支援的產品與這份掛載的範圍沒有交集，目前沒有任何站台會收到這組設定">
          目前不生效
        </small>
        <span class="spacer" />
        <button v-if="canEdit" type="button" class="linkish" @click="emit('scope', block)">
          ✎ 範圍
        </button>
        <button v-if="canEdit" type="button" class="linkish danger" @click="emit('detach', block)">
          卸載
        </button>
      </div>
      <p v-if="block.description" class="ac-desc">{{ block.description }}</p>

      <div v-for="f in block.fields" :key="f.key" class="ac-row" :class="{ dim: f.deprecated }">
        <!-- ★ 批121：**env 名不再獨立成一行**。這張卡的工作是「填值」，而環境變數名
             是給維運看的實作細節，擺在中文名底下只是讓每一列多一行雜訊（主人 2026-09-21）。
             需要對照的人把滑鼠移到名稱上就看得到；要改欄位宣告本來就得去〈產品 › 設定樣板〉。 -->
        <label class="ac-label" :title="labelTitle(f)">
          {{ f.label }}
          <small v-if="f.deprecated" class="dep">（已停用）</small>
          <small
            v-if="f.secret"
            class="lock"
            title="加密欄位：後台只顯示遮罩、下發為密文、不可進派工參數"
          >🔒</small>
        </label>

        <select
          v-if="f.type === 'bool'"
          v-model="draft[block.namespace][f.key]"
          class="ac-in"
          :disabled="!canEdit || f.deprecated"
        >
          <option value="true">開啟</option>
          <option value="false">關閉</option>
        </select>
        <select
          v-else-if="f.type === 'string' && (f.options?.length ?? 0) > 0 && !f.secret"
          v-model="draft[block.namespace][f.key]"
          class="ac-in"
          :disabled="!canEdit || f.deprecated"
        >
          <option v-for="o in f.options ?? []" :key="o" :value="o">{{ o }}</option>
        </select>
        <input
          v-else-if="f.type === 'number'"
          v-model="draft[block.namespace][f.key]"
          type="number"
          class="ac-in"
          :class="{ bad: outOfRange(block, f.key) }"
          :min="f.min ?? undefined"
          :max="f.max ?? undefined"
          :disabled="!canEdit || f.deprecated"
        />
        <!-- ★ 秘密欄位：placeholder 放遮罩，輸入框永遠是空的（空＝不改） -->
        <input
          v-else
          v-model="draft[block.namespace][f.key]"
          type="text"
          class="ac-in"
          :placeholder="
            f.secret
              ? clearing[block.namespace]?.[f.key]
                ? '將清除'
                : f.masked || '（未設定）'
              : ''
          "
          :autocomplete="f.secret ? 'off' : undefined"
          :disabled="!canEdit || f.deprecated || clearing[block.namespace]?.[f.key]"
        />

        <small class="ac-hint">
          <span v-if="outOfRange(block, f.key)" class="bad-text">
            可用範圍 {{ rangeText(f) }}
          </span>
          <template v-else-if="f.secret">
            <span v-if="!f.configured">尚未設定</span>
            <span v-else>已設定（只顯示頭尾，無法複製）</span>
            <button
              v-if="canEdit && f.configured"
              type="button"
              class="linkish"
              @click="toggleClear(block.namespace, f.key)"
            >
              {{ clearing[block.namespace]?.[f.key] ? '取消清除' : '清除' }}
            </button>
          </template>
          <span v-else-if="!f.configured">未設定（用預設值）</span>
          <span v-else-if="rangeText(f)">{{ rangeText(f) }}</span>
        </small>
      </div>

      <div class="ac-block-foot">
        <UiButton
          v-if="canEdit"
          size="sm"
          variant="primary"
          :disabled="!dirty(block)"
          @click="save(block)"
        >
          儲存 {{ block.namespace }} 的值
        </UiButton>
      </div>
    </div>

    <p v-if="blocks.length" class="ac-note">
      設定會在下一次 agent 心跳（最多 60 秒）後生效於「新開的站」；既有站台要套用，
      請到下方站台列按〈改設定〉派工。🔒 加密欄位下發前會加密，agent 與站台的
      <code>.env</code> 都只看得到密文。
    </p>
  </div>
</template>

<style scoped>
.ac {
  display: grid;
  gap: 14px;
}
.ac-block {
  display: grid;
  gap: 8px;
  border: 1px solid var(--border);
  border-radius: var(--r-md, 8px);
  padding: 10px 12px;
}
.ac-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: var(--fs-13);
}
.ns {
  font-family: ui-monospace, monospace;
  font-size: var(--fs-11);
  background: var(--bg);
  border-radius: 999px;
  padding: 1px 8px;
  color: var(--fg-3);
}
.muted {
  color: var(--fg-3);
  font-size: var(--fs-11);
}
.warn {
  color: var(--warning, #b8860b);
  font-size: var(--fs-11);
}
.ac-desc {
  margin: 0;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.spacer {
  flex: 1;
}
.ac-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}
.ac-row.dim {
  opacity: 0.6;
}
.ac-label {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  font-size: var(--fs-13);
}
.lock {
  font-size: var(--fs-11);
  cursor: help;
}
.ac-in {
  width: 100%;
  min-width: 0;
}
.ac-in.bad {
  border-color: var(--danger);
}
.ac-hint {
  color: var(--fg-4);
  font-size: var(--fs-11);
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.ac-block-foot {
  display: flex;
  justify-content: flex-end;
}
.bad-text {
  color: var(--danger);
}
.dep {
  color: var(--fg-4);
}
.linkish {
  background: none;
  border: 0;
  padding: 0;
  color: var(--accent-500);
  cursor: pointer;
  font-size: var(--fs-11);
}
.linkish.danger {
  color: var(--danger);
}
.ac-empty,
.ac-note {
  margin: 0;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
</style>
